import { ApiError } from "@repo/api-client";
import { useAuth } from "@repo/auth";
import { Tabs } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { canAccessVenue } from "../../shell/roles";
import { ClosuresCalendar } from "./ClosuresCalendar";
import { PolicyForm } from "./PolicyForm";
import { ServiceForm } from "./ServiceForm";
import { WeeklyGridEditor, type SaveResult } from "./WeeklyGridEditor";
import {
  scheduleApi,
  scheduleKeys,
  rejectedWindowIndices,
  type BookingPolicyResponse,
  type ServiceRequest,
} from "./scheduleApi";
import type { WindowDraft } from "./scheduleRules";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/**
 * The `/v/:venueId/settings/schedule` screen (features/opening-hours-and-services.md). Managers edit
 * services, the weekly grid, closures and the booking policy; waiters get a READ-ONLY hours panel
 * (advisory — the server re-enforces MANAGE_VENUE_CONFIG on every write). All queries key under
 * `["venue", venueId, "services", …]` so the realtime `service.*`/`hours.*` events invalidate them
 * (eventQueryKeyMap). The server is authoritative: a 422 rejection highlights the offending windows and
 * a 409 offers reload-current.
 */
export function ScheduleSettings() {
  const { venueId, venue } = useVenue();
  const { claims } = useAuth();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canEdit = canAccessVenue(claims, venueId, "MANAGER");
  const [defaultsNotice, setDefaultsNotice] = useState<string | null>(null);

  const services = useQuery({
    queryKey: scheduleKeys.services(venueId),
    queryFn: () => scheduleApi.listServices(venueId),
  });
  const schedule = useQuery({
    queryKey: scheduleKeys.schedule(venueId),
    queryFn: () => scheduleApi.getSchedule(venueId),
  });
  const policy = useQuery({
    queryKey: scheduleKeys.policy(venueId),
    queryFn: () => scheduleApi.getPolicy(venueId),
  });
  const exceptions = useQuery({
    queryKey: scheduleKeys.exceptions(venueId),
    queryFn: () => scheduleApi.listExceptions(venueId),
  });

  const serviceList = (services.data ?? []).map((s) => ({ id: s.id, nameFr: s.nameFr }));

  async function saveSchedule(version: number, windows: WindowDraft[]): Promise<SaveResult> {
    try {
      const saved = await scheduleApi.saveSchedule(venueId, version, windows);
      await qc.invalidateQueries({ queryKey: scheduleKeys.schedule(venueId) });
      return { ok: true, version: saved.version };
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) return { ok: false, status: 409 };
      if (err instanceof ApiError && err.status === 422)
        return { ok: false, status: 422, rejectedIndices: rejectedWindowIndices(err.problem) };
      throw err;
    }
  }

  async function savePolicy(
    body: BookingPolicyResponse,
  ): Promise<{ ok: boolean; status?: number }> {
    try {
      await scheduleApi.savePolicy(venueId, body);
      await qc.invalidateQueries({ queryKey: scheduleKeys.policy(venueId) });
      return { ok: true };
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) return { ok: false, status: 409 };
      throw err;
    }
  }

  async function createService(body: ServiceRequest): Promise<void> {
    const created = await scheduleApi.createService(venueId, body);
    await qc.invalidateQueries({ queryKey: scheduleKeys.services(venueId) });
    const a = created.defaultsApplied;
    setDefaultsNotice(
      a.turnTime || a.slotGranularity || a.leadTime || a.horizon
        ? t("schedule.service.defaultsApplied")
        : null,
    );
  }

  async function setException(date: string, kind: "CLOSED", reason: string | null): Promise<void> {
    await scheduleApi.setException(venueId, date, { kind, reason, version: 0 });
    await qc.invalidateQueries({ queryKey: scheduleKeys.exceptions(venueId) });
  }

  async function deleteException(date: string): Promise<void> {
    await scheduleApi.deleteException(venueId, date);
    await qc.invalidateQueries({ queryKey: scheduleKeys.exceptions(venueId) });
  }

  if (services.isLoading || schedule.isLoading) {
    return (
      <PageScaffold title={t("schedule.title")}>
        <p>{t("schedule.loading")}</p>
      </PageScaffold>
    );
  }

  const windows: WindowDraft[] = (schedule.data?.windows ?? []).map((w) => ({
    dayOfWeek: w.dayOfWeek,
    startTime: w.startTime.slice(0, 5),
    endTime: w.endTime.slice(0, 5),
    crossesMidnight: w.crossesMidnight,
    serviceId: w.serviceId,
  }));

  return (
    <PageScaffold title={`${t("schedule.title")} — ${venue.displayName}`}>
      <div className={css({ display: "flex", flexDirection: "column", gap: "8" })}>
        {canEdit ? (
          // The four editors used to stack in one long scroll (features/opening-hours-and-services.md).
          // Tab them so a manager sees one focused task at a time — Ark keeps every panel mounted, so each
          // editor's in-progress state (the weekly grid especially) survives switching tabs.
          <Tabs.Root defaultValue="services">
            <Tabs.List>
              <Tabs.Trigger value="services">{t("schedule.tab.services")}</Tabs.Trigger>
              <Tabs.Trigger value="hours">{t("schedule.tab.hours")}</Tabs.Trigger>
              <Tabs.Trigger value="closures">{t("schedule.tab.closures")}</Tabs.Trigger>
              <Tabs.Trigger value="policy">{t("schedule.tab.policy")}</Tabs.Trigger>
              <Tabs.Indicator />
            </Tabs.List>
            <Tabs.Content value="services">
              <div className={css({ display: "flex", flexDirection: "column", gap: "6" })}>
                <ServiceForm onSubmit={createService} defaultsNotice={defaultsNotice} />
                <ServiceList services={services.data ?? []} />
              </div>
            </Tabs.Content>
            <Tabs.Content value="hours">
              <WeeklyGridEditor
                services={serviceList}
                initialWindows={windows}
                initialVersion={schedule.data?.version ?? 0}
                onSave={saveSchedule}
                onReload={() =>
                  void qc.invalidateQueries({ queryKey: scheduleKeys.schedule(venueId) })
                }
              />
            </Tabs.Content>
            <Tabs.Content value="closures">
              <ClosuresCalendar
                exceptions={exceptions.data ?? []}
                onSet={setException}
                onDelete={deleteException}
              />
            </Tabs.Content>
            <Tabs.Content value="policy">
              {policy.data ? (
                <PolicyForm
                  policy={policy.data}
                  onSave={savePolicy}
                  onReload={() =>
                    void qc.invalidateQueries({ queryKey: scheduleKeys.policy(venueId) })
                  }
                />
              ) : null}
            </Tabs.Content>
          </Tabs.Root>
        ) : (
          <ReadOnlyHours windows={windows} services={serviceList} />
        )}
      </div>
    </PageScaffold>
  );
}

function ServiceList({
  services,
}: {
  services: Array<{
    id: string;
    nameFr: string;
    onlineBookable: boolean;
    partyMin: number;
    partyMax: number;
  }>;
}) {
  const { t } = useTranslation();
  if (services.length === 0)
    return (
      <p className={css({ color: "fg.muted", fontSize: "sm" })}>{t("schedule.service.empty")}</p>
    );
  return (
    <ul
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "1",
        listStyle: "none",
        p: "0",
      })}
    >
      {services.map((s) => (
        <li key={s.id} className={css({ display: "flex", gap: "3", fontSize: "sm" })}>
          <span className={css({ fontWeight: "medium" })}>{s.nameFr}</span>
          <span className={css({ color: "fg.muted" })}>
            {s.partyMin}–{s.partyMax}
          </span>
          {s.onlineBookable ? (
            <span className={css({ color: "fg.success" })}>{t("schedule.service.online")}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** The waiter read-only hours panel — the grid grouped by day, no controls (R5, advisory). */
function ReadOnlyHours({
  windows,
  services,
}: {
  windows: WindowDraft[];
  services: Array<{ id: string; nameFr: string }>;
}) {
  const { t } = useTranslation();
  const nameOf = (id: string) => services.find((s) => s.id === id)?.nameFr ?? "";
  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
      <h3 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
        {t("schedule.weekly.title")}
      </h3>
      <dl className={css({ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2" })}>
        {DAY_KEYS.map((day, i) => {
          const forDay = windows.filter((w) => w.dayOfWeek === i + 1);
          return (
            <div key={day} className={css({ display: "contents" })}>
              <dt className={css({ color: "fg.muted" })}>{t(`schedule.day.${day}`)}</dt>
              <dd>
                {forDay.length === 0
                  ? t("schedule.weekly.closedDay")
                  : forDay
                      .map((w) =>
                        `${w.startTime}–${w.endTime}${w.crossesMidnight ? " (+1)" : ""} ${nameOf(w.serviceId)}`.trim(),
                      )
                      .join(", ")}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}
